
import * as THREE from 'three';

export interface Emitter {
  position: THREE.Vector3;
  particles: THREE.Vector3[];
  life: number;
}

/**
 * Creates the particle geometry for the data wave
 * @param particleCount Number of particles in the wave
 * @param waveWidth Width of the wave
 * @param waveDepth Depth of the wave
 * @returns Object containing geometry, positions, and colors
 */
export const createWaveParticles = (particleCount: number, waveWidth: number, waveDepth: number) => {
  const particleGeometry = new THREE.BufferGeometry();
  const positions = new Float32Array(particleCount * 3);
  const colors = new Float32Array(particleCount * 3);
  
  // Color palette for gradient
  const colorPalette = [
    new THREE.Color('#60a5fa'), // Light blue
    new THREE.Color('#3b82f6'), // Medium blue
    new THREE.Color('#1e40af')  // Dark blue
  ];
  
  // Generate initial positions and colors
  for (let i = 0; i < particleCount; i++) {
    // Create grid-like distribution for wave
    const x = (Math.random() - 0.5) * waveWidth;
    const z = (Math.random() - 0.5) * waveDepth;
    // Y will be computed in animation loop
    const y = 0;
    
    // Set initial positions
    positions[i * 3] = x;
    positions[i * 3 + 1] = y;
    positions[i * 3 + 2] = z;
    
    // Set colors based on position (z-depth gradient)
    const colorIndex = Math.floor(Math.random() * 3);
    const color = colorPalette[colorIndex];
    colors[i * 3] = color.r;
    colors[i * 3 + 1] = color.g;
    colors[i * 3 + 2] = color.b;
  }
  
  particleGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  particleGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  
  return {
    geometry: particleGeometry,
    positions,
    colors
  };
};

/**
 * Updates the wave animation
 * @param positions Float32Array of particle positions
 * @param particleCount Number of particles
 * @param time Current animation time
 * @param amplitude Wave amplitude
 * @param frequency Wave frequency
 * @param speed Animation speed
 * @param mouseX Mouse X position (normalized)
 * @param mouseY Mouse Y position (normalized)
 */
export const updateWaveAnimation = (
  positions: Float32Array,
  particleCount: number,
  time: number,
  amplitude: number,
  frequency: number,
  speed: number,
  mouseX: number,
  mouseY: number
) => {
  // Wave animation parameters
  const mouseInfluence = 0.5;
  
  // Update wave particles
  for (let i = 0; i < particleCount; i++) {
    const ix = i * 3;
    const iy = i * 3 + 1;
    const iz = i * 3 + 2;
    
    const x = positions[ix];
    const z = positions[iz];
    
    // Create original wave pattern with multiple sine waves
    let y = amplitude * Math.sin(frequency * x + time * speed);
    y += amplitude * 0.5 * Math.sin(frequency * 2 * z + time * speed * 1.5);
    
    // Add original mouse interaction
    y += mouseY * mouseInfluence * Math.exp(-((x - mouseX * 5) ** 2 + z ** 2) / 10);
    
    positions[iy] = y;
  }
};

/**
 * Updates emitters and their particles
 * @param emitters Array of emitters
 * @param delta Time delta
 */
export const updateEmitters = (emitters: Emitter[], delta: number) => {
  // Update existing emitters
  for (let i = emitters.length - 1; i >= 0; i--) {
    const emitter = emitters[i];
    emitter.life -= delta;
    
    // Spawn new particles from emitter
    if (emitter.life > 0 && Math.random() < 0.3) {
      emitter.particles.push(
        new THREE.Vector3(
          emitter.position.x + (Math.random() - 0.5) * 0.1,
          emitter.position.y + (Math.random() - 0.5) * 0.1,
          emitter.position.z + (Math.random() - 0.5) * 0.1
        )
      );
    }
    
    // Remove dead emitter
    if (emitter.life <= 0) {
      emitters.splice(i, 1);
    }
  }
};

/**
 * Creates a new emitter at a random position
 * @param waveWidth Width of the wave
 * @param waveDepth Depth of the wave
 * @param amplitude Wave amplitude
 * @param frequency Wave frequency
 * @param time Current animation time
 * @param speed Animation speed
 * @returns New emitter object
 */
export const createEmitter = (
  waveWidth: number,
  waveDepth: number,
  amplitude: number,
  frequency: number,
  time: number,
  speed: number
): Emitter => {
  const x = (Math.random() - 0.5) * waveWidth * 0.8;
  const z = (Math.random() - 0.5) * waveDepth * 0.8;
  const y = amplitude * Math.sin(frequency * x + time * speed);
  
  return {
    position: new THREE.Vector3(x, y, z),
    particles: [],
    life: 3.0 // Emitter lifetime in seconds
  };
};
